// Import dependencies
import { builderFetch } from '@automator/builder/utils';
import { html } from 'lit';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { confirm } from '@automator/shared/utils/confirm';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import recipe builder services
import { getLoop } from '@automator/builder/services/item/loop';

/**
 * Deeltes a loop from the current recipe
 *
 * @param {Number} loopID The loop ID (post ID)
 * @return {Promise} The apiFetch promise
 */
const deleteLoop = async ( loopID ) => {
	// Event name
	const eventName = 'uap/builder/core/item/loop/delete-loop';

	// Get the loop
	const loop = getLoop( loopID );

	// If the item is not define, stop here
	if ( isEmpty( loop ) ) {
		throw new Error( `deleteLoop: The loop ${ loopID } doesn't exist` );
	}

	/**
	 * Checks if an array of items have at least one enabled item inside
	 *
	 * @param {Array} items An array of items
	 * @return {Boolean} Whether it has at least one live item
	 */
	const itemsHaveLiveItem = items => items.some(
		item => item.items ? itemsHaveLiveItem( item.items ) : item.is_item_on
	);

	// Check if the recipe is live and there is at least one live item, and show a warning message
	if (
		// Check if the recipe is live
		UncannyAutomator._recipe.is_recipe_on &&
		// Check if the loop contains live items
		itemsHaveLiveItem( loop.items )
	) {
		await confirm( {
			content: html`
				<uo-alert type="warning">
					${ escapeHTML( __( 'Deleting a loop from an active recipe might lead to unexpected behaviors, so all actions inside will be switched to draft upon deletion.', 'uncanny-automator' ) ) }
				</uo-alert>
			`,
			confirmButtonLabel: escapeHTML( __( 'Delete loop', 'uncanny-automator' ) )
		} );
	} else {
		await confirm( {
			content: escapeHTML( __( 'This action cannot be undone. Actions within the loop will be moved outside of the loop.', 'uncanny-automator' ) ),
			confirmButtonLabel: escapeHTML( __( 'Delete loop', 'uncanny-automator' ) )
		} );
	}

	// Save the recipe title
	return builderFetch( {
		eventName: eventName,
		endpoint: `loop/${ UncannyAutomator._recipe.recipe_id }`,
		method: 'POST',
		data: {
			action: 'delete_loop_block',
			// eslint-disable-next-line camelcase
			loop_id: loopID
		},
		snackbar: {
			loading: escapeHTML( __( 'Deleting loop', 'uncanny-automator' ) ),
			success: escapeHTML( __( 'Loop deleted successfully', 'uncanny-automator' ) ),
		}
	} ).then( response => {
		// Check if the call was successful
		if ( response.success ) {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( eventName, {
				detail: {
					loopID: loopID
				}
			} ) );
		}

		return response;
	} );
}

export default deleteLoop;
